import { useState } from "react";
import type { CustomerGroup } from "../services/customerGroupsApi";
import { useUpdateCustomerGroup } from "../hooks/useCustomerGroups";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Pencil } from "lucide-react";

const PAYMENT_TERMS = [
  "AdvancePayment",
  "COD",
  "Net7",
  "Net15",
  "Net30",
  "Net45",
  "Net60",
  "Net90",
];

interface EditGroupDialogProps {
  group: CustomerGroup;
}

export const EditGroupDialog = ({ group }: EditGroupDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: group.name,
    description: group.description,
    defaultPaymentTerms: group.defaultPaymentTerms,
    defaultCreditLimit: group.defaultCreditLimit,
    defaultDiscountPercentage: group.defaultDiscountPercentage,
    isActive: group.isActive,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setFormData({
        name: group.name,
        description: group.description,
        defaultPaymentTerms: group.defaultPaymentTerms,
        defaultCreditLimit: group.defaultCreditLimit,
        defaultDiscountPercentage: group.defaultDiscountPercentage,
        isActive: group.isActive,
      });
      setFieldErrors({});
    }
    setOpen(nextOpen);
  };

  const notify = useNotification();
  const updateMutation = useUpdateCustomerGroup(group.id);
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof formData,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Save group changes?",
      description: `Changes to “${group.name}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        updateMutation
          .mutateAsync(formData)
          .then(() => {
            notify.success("Customer group updated successfully");
            setOpen(false);
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) {
              setFieldErrors(fe);
            }
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer Group</DialogTitle>
          <DialogDescription>
            Update group settings and defaults
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="name">
                  Group Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  disabled={updateMutation.isPending}
                />
                {fieldErrors.name && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.name.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="isActive">Status</FieldLabel>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      handleChange("isActive", checked)
                    }
                    disabled={updateMutation.isPending}
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active
                  </Label>
                </div>
              </Field>
            </div>

            <Field className="mt-4">
              <FieldLabel htmlFor="description">
                Description <span className="text-red-500">*</span>
              </FieldLabel>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                required
                disabled={updateMutation.isPending}
                placeholder="Describe this customer group"
                className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              />
              {fieldErrors.description && (
                <div className="text-red-500 text-xs mt-1">
                  {fieldErrors.description.join(", ")}
                </div>
              )}
            </Field>
          </div>

          {/* Default Settings */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Default Settings</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="defaultPaymentTerms">
                  Payment Terms <span className="text-red-500">*</span>
                </FieldLabel>
                <Select
                  value={formData.defaultPaymentTerms}
                  onValueChange={(v) => handleChange("defaultPaymentTerms", v)}
                >
                  <SelectTrigger id="defaultPaymentTerms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term} value={term}>
                        {term.replace(/([A-Z])/g, " $1").trim()}{" "}
                        {/* Formats "Net7" to "Net 7" */}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel htmlFor="defaultCreditLimit">
                  Credit Limit <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="defaultCreditLimit"
                  type="number"
                  value={formData.defaultCreditLimit}
                  onChange={(e) =>
                    handleChange(
                      "defaultCreditLimit",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  required
                  disabled={updateMutation.isPending}
                />
                {fieldErrors.defaultCreditLimit && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.defaultCreditLimit.join(", ")}
                  </div>
                )}
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="defaultDiscountPercentage">
                  Default Discount Percentage{" "}
                  <span className="text-red-500">*</span>
                </FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="defaultDiscountPercentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.defaultDiscountPercentage}
                    onChange={(e) =>
                      handleChange(
                        "defaultDiscountPercentage",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    required
                    disabled={updateMutation.isPending}
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                {fieldErrors.defaultDiscountPercentage && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.defaultDiscountPercentage.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
